import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Heart, BadgeCheck, Star, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { EASE_LUXE } from "./Reveal";

export function prettyCity(slug) {
  return (slug || "")
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export default function CompanionCard({ companion, c, index = 0 }) {
  // Support both `companion` and `c` prop names (some pages pass `c`).
  const data = companion || c;
  const { user, favSlugs, toggleFavourite } = useAuth();
  const isFav = favSlugs?.includes(data.slug);

  // Upcoming tour ribbon — first tour starting in the next 30 days or currently live.
  // Considers both new tours[] array AND legacy tour {} field.
  const today = new Date().toISOString().slice(0, 10);
  const in30 = new Date(Date.now() + 30 * 86400_000).toISOString().slice(0, 10);
  const allTours = [
    ...(data.tours || []),
    ...(data.tour?.city ? [data.tour] : []),
  ];
  const upcomingTour = allTours
    .filter((t) => (t.end_date || "") >= today && (t.start_date || "") <= in30)
    .sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""))[0];

  // 3D tilt
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 200, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 200, damping: 18 });

  const handleMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => {
    mx.set(0);
    my.set(0);
  };

  const onFav = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error("Please sign in to save favourites.");
      return;
    }
    const fav = await toggleFavourite(data.slug);
    toast.success(fav ? "Added to favourites" : "Removed from favourites");
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9, ease: EASE_LUXE, delay: (index % 5) * 0.07 }}
      data-testid={`companion-card-${data.slug}`}
      style={{ perspective: 1000 }}
    >
      <motion.div
        onMouseMove={handleMove}
        onMouseLeave={reset}
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
      >
        <Link to={`/companion/${data.slug}`} className="group block">
          <div className="clip-frame relative aspect-[3/4] bg-velvet-coal ring-1 ring-transparent transition-shadow duration-500 group-hover:ring-velvet-rose group-hover:shadow-[0_0_40px_-8px_rgba(193,95,118,0.8)]">
            <img
              src={data.hero_image}
              alt={data.name}
              loading="lazy"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent opacity-85" />
            <div className="pointer-events-none absolute inset-0 bg-velvet-rose/0 transition-colors duration-500 group-hover:bg-velvet-rose/12" />

            <button
              onClick={onFav}
              data-testid={`favourite-button-${data.slug}`}
              className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/40 backdrop-blur-md transition-colors duration-300 hover:bg-black/70"
              style={{ transform: "translateZ(30px)" }}
            >
              <Heart size={14} className={isFav ? "fill-velvet-rose text-velvet-rose" : "text-white"} />
            </button>

            {data.featured && (
              <span className="absolute left-3 top-3 bg-velvet-gold px-2.5 py-1 text-[9px] uppercase tracking-luxe text-black">
                Featured
              </span>
            )}

            {upcomingTour && (
              <span
                data-testid={`tour-badge-${data.slug}`}
                className={`absolute left-3 ${data.featured ? "top-11" : "top-3"} flex items-center gap-1 bg-velvet-rose/95 px-2.5 py-1 text-[9px] uppercase tracking-luxe text-white backdrop-blur`}
              >
                <MapPin size={9} />
                {upcomingTour.start_date <= today
                  ? `Touring ${prettyCity(upcomingTour.city)}`
                  : `${prettyCity(upcomingTour.city)} · ${upcomingTour.start_date.slice(5)}`}
              </span>
            )}

            <div className="absolute inset-x-0 bottom-0 p-4" style={{ transform: "translateZ(24px)" }}>
              <div className="flex items-center gap-1.5">
                <h3 className="font-heading text-xl font-semibold text-white transition-colors duration-500 group-hover:text-velvet-rose">
                  {data.name}
                </h3>
                {data.verified && <BadgeCheck size={14} className="text-velvet-gold" />}
              </div>
              <div className="mt-1 flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-luxe text-white/60">
                  {prettyCity(data.city)} &middot; {data.age}
                </p>
                <span className="flex items-center gap-1 text-[11px] text-velvet-gold">
                  <Star size={11} className="fill-velvet-gold" /> {data.rating}
                </span>
              </div>
            </div>
          </div>
        </Link>
      </motion.div>
    </motion.div>
  );
}
